/**
 * Polyline simplification using Ramer-Douglas-Peucker algorithm.
 * Ramer-Douglas-Peucker implementation for freehand line smoothing.
 */

type Point3D = [number, number, number];

/**
 * Simplifies a polyline using the Ramer-Douglas-Peucker algorithm
 * @param points - Array of points [x, y, z] representing the polyline
 * @param epsilon - Tolerance value in screen pixels (higher = more simplification, typical: 1-5)
 * @param closed - Whether the polyline is a closed contour
 * @param screenDistanceToWorld - Function to convert screen distance to world distance
 * @returns Simplified array of points
 */
export function simplifyPolyline(
  points: Point3D[],
  epsilon: number,
  closed: boolean,
  screenDistanceToWorld: (d: number) => number,
): Point3D[] {
  if (points.length <= 2) {
    return points;
  }

  const epsilonWCS = screenDistanceToWorld(epsilon);

  if (closed) {
    return simplifyClosedPolyline(points, epsilonWCS);
  }

  return ramerDouglasPeucker(points, epsilonWCS);
}

/**
 * Simplifies a closed polyline (contour)
 * Handles the wrap-around connection between last and first point
 */
function simplifyClosedPolyline(points: Point3D[], epsilon: number): Point3D[] {
  if (points.length <= 3) {
    return points;
  }

  // Check if first and last points are the same (explicitly closed)
  const isExplicitlyClosed =
    points[0][0] === points[points.length - 1][0] &&
    points[0][1] === points[points.length - 1][1];

  // Work with the polyline without the duplicate closing point
  const workingPoints = isExplicitlyClosed ? points.slice(0, -1) : points;

  if (workingPoints.length <= 2) {
    return points;
  }

  // Find the point furthest from any line segment in the polygon
  let maxDistance = 0;
  let maxIndex = 0;

  for (let i = 0; i < workingPoints.length; i++) {
    const next = (i + 1) % workingPoints.length;
    const distance = perpendicularDistance(
      workingPoints[i],
      workingPoints[0],
      workingPoints[next],
    );
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }

  // Split at the furthest point and simplify as two segments
  const segment1 = workingPoints.slice(0, maxIndex + 1);
  const segment2 = [...workingPoints.slice(maxIndex), workingPoints[0]];

  const simplified1 = ramerDouglasPeucker(segment1, epsilon);
  const simplified2 = ramerDouglasPeucker(segment2, epsilon);

  // Combine segments, removing duplicate points at junctions
  const result = [...simplified1.slice(0, -1), ...simplified2.slice(0, -1)];

  // Re-add explicit closing point if original had it
  if (isExplicitlyClosed && result.length > 0) {
    result.push([...result[0]] as Point3D);
  }

  return result;
}

/**
 * Calculates perpendicular distance from a point to a line segment
 * Works with [x, y, z] points, ignoring z coordinate
 */
function perpendicularDistance(
  point: Point3D,
  lineStart: Point3D,
  lineEnd: Point3D,
): number {
  const dx = lineEnd[0] - lineStart[0];
  const dy = lineEnd[1] - lineStart[1];

  // Line segment length squared
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    // Line start and end are the same point
    const pdx = point[0] - lineStart[0];
    const pdy = point[1] - lineStart[1];
    return Math.sqrt(pdx * pdx + pdy * pdy);
  }

  // Calculate perpendicular distance using cross product
  const numerator = Math.abs(
    dy * point[0] -
      dx * point[1] +
      lineEnd[0] * lineStart[1] -
      lineEnd[1] * lineStart[0],
  );

  return numerator / Math.sqrt(lengthSquared);
}

/**
 * Ramer-Douglas-Peucker algorithm implementation
 * Works with [x, y, z] points, preserving z values
 */
function ramerDouglasPeucker(points: Point3D[], epsilon: number): Point3D[] {
  if (points.length <= 2) {
    return points;
  }

  // Find the point with maximum distance from the line segment
  let maxDistance = 0;
  let maxIndex = 0;
  const end = points.length - 1;

  for (let i = 1; i < end; i++) {
    const distance = perpendicularDistance(points[i], points[0], points[end]);
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }

  // If max distance is greater than epsilon, recursively simplify
  if (maxDistance > epsilon) {
    // Recursive call on both segments
    const left = ramerDouglasPeucker(points.slice(0, maxIndex + 1), epsilon);
    const right = ramerDouglasPeucker(points.slice(maxIndex), epsilon);

    // Combine results (remove duplicate point at junction)
    return [...left.slice(0, -1), ...right];
  } else {
    // All points are within epsilon, return only endpoints
    return [points[0], points[end]];
  }
}
